/**
 * P-305 · the Tasks list's **column widths**, per user and per tab.
 *
 * The report was Nico's, 2026-09-24: *"give the possibility to enlarge the columns, for example
 * the first one"*. A long item name or a long board name was ellipsised with no way out, because
 * `tasks.css` declares the six columns as fixed custom properties and only the item flexes.
 *
 * ## What a stored width **is**
 *
 * `--ez-tasks-<key>-w` is the column's flex *basis*, not its size: five of the six shrink
 * (`flex: 0 1`) and the item grows (`flex: 1 1`), so at 1440 the item is painted 384 px wide from
 * a 200 px basis and at 1024 every column is painted narrower than its basis. A drag therefore
 * cannot simply write the basis and expect the column to be that wide — `+200` on a growing
 * column changes nothing at all, because the grow term gives back exactly what the basis took.
 *
 * So a width the user has set makes that column **rigid**: the basis *and* the floor become the
 * number they dragged to (and, for the item, its grow term becomes 0). The columns they have not
 * touched keep flexing and absorb the difference, which is what keeps the line inside the page.
 * Resetting a column deletes its entry, and the flex arithmetic is back exactly as it was — a
 * reset is not "the default width", it is *no width*, which is not the same thing for a column
 * whose painted size depends on the viewport.
 *
 * ## Where they live
 *
 * `preferences` key `tasks.columnWidths`, written with `preferences.setMany` through the page's
 * own `TasksSource` port (`types.ts`), shaped `{ [tab]: { [column]: px } }`. Per **user**: the
 * Tasks list is personal — it has no board revision and no owner — so there is no permission
 * check to make and nothing for a second person to see.
 *
 * Every function here is pure. The gesture is `lib/use-column-resize.ts` (the board grid's), the
 * storage is the port, and this file is the arithmetic the two of them meet in.
 */
import type { CSSProperties } from 'react';
import { TASK_COLUMN_KEYS, type TaskColumnKey } from './columns';
import { TASK_TABS, type TaskColumnWidthsView, type TaskTab } from './types';

/** The preference this module owns. The key set is closed (`services/preferences.ts`). */
export const TASK_COLUMN_WIDTHS_KEY = 'tasks.columnWidths';

/**
 * The floor every column shares, the ceiling every column shares, and the item's own floor.
 *
 * The card's numbers. The item's is higher than the rest because it is the column the whole row
 * is read from: at 80 px it holds about six characters of an item name plus its breadcrumb, which
 * is not a narrow column but an unusable one.
 */
export const TASK_COLUMN_WIDTH_MIN = 80;
export const TASK_COLUMN_WIDTH_MAX = 1200;
export const TASK_ITEM_COLUMN_WIDTH_MIN = 200;

/**
 * The bases `tasks.css` declares, restated so a reset has something to name in a menu and so the
 * unit tests assert against the stylesheet rather than against themselves.
 *
 * They are **not** what a column is painted at (see the note above), which is why nothing here
 * writes them: a reset removes the entry instead.
 */
export const TASK_COLUMN_DEFAULT_WIDTHS: Record<TaskColumnKey, number> = {
  item: 200,
  board: 160,
  person: 190,
  status: 140,
  due: 140,
  assigned: 88,
};

/** One tab's widths: only the columns this user has actually dragged. */
export type TaskColumnWidths = Partial<Record<TaskColumnKey, number>>;
/** Every tab's, which is the shape the preference stores. */
export type TaskColumnWidthsByTab = Partial<Record<TaskTab, TaskColumnWidths>>;

export function taskColumnWidthFloor(key: TaskColumnKey): number {
  return key === 'item' ? TASK_ITEM_COLUMN_WIDTH_MIN : TASK_COLUMN_WIDTH_MIN;
}

/**
 * The drag's clamp — `[floor, 1200]`, and never `NaN`.
 *
 * Clamped rather than refused, for the reason `shell.sidebarWidth` is: a resize drag must never
 * fail. A pointer dragged past the ceiling stops at the ceiling instead of writing nothing.
 */
export function clampTaskColumnWidth(key: TaskColumnKey, width: number): number {
  const floor = taskColumnWidthFloor(key);
  if (!Number.isFinite(width)) return floor;
  return Math.min(Math.max(Math.round(width), floor), TASK_COLUMN_WIDTH_MAX);
}

export function taskColumnWidthsFor(all: TaskColumnWidthsByTab, tab: TaskTab): TaskColumnWidths {
  return all[tab] ?? {};
}

/** A width the user dragged to, clamped, in **one** tab: the other two are untouched. */
export function withTaskColumnWidth(
  all: TaskColumnWidthsByTab,
  tab: TaskTab,
  key: TaskColumnKey,
  width: number,
): TaskColumnWidthsByTab {
  return { ...all, [tab]: { ...taskColumnWidthsFor(all, tab), [key]: clampTaskColumnWidth(key, width) } };
}

/**
 * **Reset**: the entry is deleted, not set to `TASK_COLUMN_DEFAULT_WIDTHS[key]`.
 *
 * Writing the default back would pin the column to its *basis* — 200 px for an item that the
 * stylesheet paints 384 px wide at 1440 — so a "reset" would visibly shrink it. The absence of an
 * entry is the default.
 */
export function withoutTaskColumnWidth(
  all: TaskColumnWidthsByTab,
  tab: TaskTab,
  key: TaskColumnKey,
): TaskColumnWidthsByTab {
  const current = taskColumnWidthsFor(all, tab);
  if (current[key] === undefined) return all;
  const next: TaskColumnWidths = { ...current };
  delete next[key];
  return { ...all, [tab]: next };
}

const COLUMN_KEY_SET = new Set<string>(TASK_COLUMN_KEYS);
const TAB_SET = new Set<string>(TASK_TABS);

/**
 * Whatever the preference hands back, reduced to widths this page can use.
 *
 * An unknown tab, an unknown column, a string where a number should be and a value outside
 * `[floor, ceiling]` are all dropped or clamped rather than thrown on: this value is written by a
 * client and read by a client, and a list that fails to render because somebody's stored blob is
 * a version behind is a worse answer than a list with default columns.
 */
export function coerceTaskColumnWidths(raw: unknown): TaskColumnWidthsByTab {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: TaskColumnWidthsByTab = {};
  for (const [tab, columns] of Object.entries(raw as Record<string, unknown>)) {
    if (!TAB_SET.has(tab)) continue;
    if (columns === null || typeof columns !== 'object' || Array.isArray(columns)) continue;
    const widths: TaskColumnWidths = {};
    for (const [key, width] of Object.entries(columns as Record<string, unknown>)) {
      if (!COLUMN_KEY_SET.has(key)) continue;
      if (typeof width !== 'number' || !Number.isFinite(width)) continue;
      widths[key as TaskColumnKey] = clampTaskColumnWidth(key as TaskColumnKey, width);
    }
    if (Object.keys(widths).length > 0) out[tab as TaskTab] = widths;
  }
  return out;
}

/** The wire shape (`types.ts`): plain records, which is what the preference stores. */
export function taskColumnWidthsToView(all: TaskColumnWidthsByTab): TaskColumnWidthsView {
  const out: TaskColumnWidthsView = {};
  for (const [tab, widths] of Object.entries(all)) {
    if (widths === undefined) continue;
    const columns: Record<string, number> = {};
    for (const [key, width] of Object.entries(widths)) {
      if (typeof width === 'number') columns[key] = width;
    }
    out[tab] = columns;
  }
  return out;
}

/**
 * One tab's widths as the custom properties the header and the rows both read.
 *
 * Three properties per resized column, which is what "rigid" costs in a flex line:
 *
 *   `--ez-tasks-<key>-w`     the basis;
 *   `--ez-tasks-<key>-min`   the floor, so the line's shrink term cannot take it back (at ≤1023
 *                            `mobile.css` sets every floor to 0, which is exactly the case where
 *                            a basis alone would be given away again);
 *   `--ez-tasks-item-grow`   0, for the item only: it is the one column that grows, and a growing
 *                            column swallows its own resize.
 *
 * Nothing is emitted for a column the user has not touched, so the stylesheet's own arithmetic
 * is untouched for it.
 */
export function taskColumnWidthStyle(widths: TaskColumnWidths): CSSProperties {
  const style: Record<string, string> = {};
  for (const key of TASK_COLUMN_KEYS) {
    const width = widths[key];
    if (typeof width !== 'number') continue;
    style[`--ez-tasks-${key}-w`] = `${width}px`;
    style[`--ez-tasks-${key}-min`] = `${width}px`;
    if (key === 'item') style['--ez-tasks-item-grow'] = '0';
  }
  return style as CSSProperties;
}
